import { renderSections } from "../utils/renderSections";

export default {
	requests: new Set(),
	isUpdating: false,
	isOpen: false,
	lastDiscountError: null,

	// Get the section ids from the sections to be re-rendered when the cart updates.
	getSectionIDs() {
		return [...document.querySelectorAll("[data-cart-section]")].map(
			(el) => el.dataset.id
		);
	},

	// Render cart-related sections.
	render(sections) {
		return renderSections(sections);
	},

	// Send data to a cart AJAX endpoint.
	// Pass { renderSections: false } to skip section re-render on intermediate
	// calls in a batch (e.g. fan-out writes where only the last call should render).
	async send(endpoint, data, options = {}) {
		const { renderSections: shouldRender = true } = options;

		this.requests.add(data);
		this.isUpdating = this.requests.size > 0;

		const config = {
			method: "POST",
		};
		const sectionIDs = shouldRender ? this.getSectionIDs() : [];

		if (data instanceof FormData) {
			if (shouldRender) {
				data.append("sections", sectionIDs.join(","));
				data.append("sections_url", window.location.pathname);
			}

			config.body = data;
		} else {
			config.headers = {
				"Content-Type": "application/json",
				Accept: "application/json",
			};

			const body = { ...data };
			if (shouldRender) {
				body.sections = sectionIDs;
				body.sections_url = window.location.pathname;
			}

			config.body = JSON.stringify(body);
		}

		let succeeded = false;

		try {
			const response = await fetch(
				`${window.Shopify.routes.root}cart/${endpoint}`,
				config
			);
			const json = await response.json();

			if (!response.ok) {
				throw new Error(json.message);
			}

			if (shouldRender && json.sections) {
				await this.render(json.sections);
			}

			succeeded = true;
		} catch (error) {
			console.error(error);
		}

		this.requests.delete(data);
		this.isUpdating = this.requests.size > 0;

		return succeeded;
	},

	// https://shopify.dev/api/ajax/reference/cart#post-locale-cart-add-js
	// Will accept an array of item(s), or a FormData object.
	add(items = []) {
		return this.send("add.js", items).then(async (result) => {
			this.isOpen = true;

			if (result) {
				// Propagate active delivery options onto the newly added line(s).
				// No-op when nothing is set.
				await this.syncDeliveryProperties();
			}

			return result;
		});
	},

	// https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
	// Best for updating the cart note or cart attributes.
	update(update = {}) {
		return this.send("update.js", update);
	},

	// https://shopify.dev/api/ajax/reference/cart#post-locale-cart-change-js
	// Best for updating quantities, properties, or selling_plans of line items.
	change(update = {}) {
		return this.send("change.js", update);
	},

	// Apply a discount code via Shopify's documented /discount/<code> endpoint.
	// /cart/update.js does not accept a `discount` parameter, so this is the
	// canonical AJAX path. After setting the cookie, we re-render cart sections
	// and verify whether the code was actually accepted.
	async applyDiscount(code) {
		this.lastDiscountError = null;
		this.requests.add("apply-discount");
		this.isUpdating = true;

		try {
			await fetch(
				`${window.Shopify.routes.root}discount/${encodeURIComponent(code)}`,
				{ method: "GET", credentials: "same-origin", redirect: "manual" }
			);
		} catch (error) {
			console.error("applyDiscount failed:", error);
		}

		this.requests.delete("apply-discount");
		this.isUpdating = this.requests.size > 0;

		// No-op /cart/update.js call to fetch fresh sections.
		await this.update({});

		// /discount/<code> silently ignores invalid codes, so verify by reading
		// the cart afterwards and surface a transient error if not applied.
		try {
			const cart = await fetch(
				`${window.Shopify.routes.root}cart.js`,
				{ credentials: "same-origin" }
			).then((r) => r.json());

			const applied = cart.discount_codes.some(
				(d) =>
					d.code &&
					d.code.toLowerCase() === code.toLowerCase() &&
					d.applicable
			);

			if (!applied) {
				this.lastDiscountError = code;
			}
		} catch (error) {
			console.error("discount verification failed:", error);
		}
	},

	// Remove all applied discount codes by POSTing an empty `discount` to
	// /cart/update.js as urlencoded — the only body shape Shopify reads.
	async removeDiscount() {
		this.lastDiscountError = null;
		this.requests.add("remove-discount");
		this.isUpdating = true;

		const sectionIDs = this.getSectionIDs();
		const body = new URLSearchParams();
		body.append("discount", "");
		body.append("sections", sectionIDs.join(","));
		body.append("sections_url", window.location.pathname);

		try {
			const response = await fetch(
				`${window.Shopify.routes.root}cart/update.js`,
				{
					method: "POST",
					headers: { "Content-Type": "application/x-www-form-urlencoded" },
					body: body.toString(),
					credentials: "same-origin",
				}
			);
			const json = await response.json();
			if (json.sections) {
				await this.render(json.sections);
			}
		} catch (error) {
			console.error("removeDiscount failed:", error);
		}

		this.requests.delete("remove-discount");
		this.isUpdating = this.requests.size > 0;
	},

	// Persist lift-gate / residential delivery flags.
	// cart.attributes is the source of truth (seeds the UI across page loads
	// and section re-renders); line-item properties are written out on every
	// item so the shipping-rate callback can read them per item.
	async setDeliveryOptions({ liftGate, residential } = {}) {
		const attributes = {
			lift_gate: liftGate ? "true" : "false",
			residential: residential ? "true" : "false",
		};

		await this.send("update.js", { attributes }, { renderSections: false });

		await this.syncDeliveryProperties();
	},

	// Fan out the current cart.attributes delivery flags onto every line item
	// as _lift_gate / _residential properties. Idempotent — skips items that
	// already match. Only the last /cart/change.js call re-renders sections.
	async syncDeliveryProperties() {
		const cart = await fetch(
			`${window.Shopify.routes.root}cart.js`
		).then((r) => r.json());

		if (!cart.items.length) return true;

		const attrs = cart.attributes || {};
		if (attrs.lift_gate === undefined && attrs.residential === undefined) {
			return true;
		}

		const liftGate = attrs.lift_gate === "true" ? "true" : "false";
		const residential = attrs.residential === "true" ? "true" : "false";

		const toUpdate = [];
		cart.items.forEach((item, index) => {
			const current = item.properties || {};
			if (
				current._lift_gate === liftGate &&
				current._residential === residential
			) {
				return;
			}
			toUpdate.push({ item, line: index + 1 });
		});

		if (!toUpdate.length) return true;

		let succeeded = true;
		for (let i = 0; i < toUpdate.length; i++) {
			const { item, line } = toUpdate[i];
			const isLast = i === toUpdate.length - 1;

			const properties = { ...(item.properties || {}) };
			properties._lift_gate = liftGate;
			properties._residential = residential;

			const result = await this.send(
				"change.js",
				{ line, properties },
				{ renderSections: isLast }
			);
			if (!result) succeeded = false;
		}

		return succeeded;
	},
};
